import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchAlerts, dismissAlert } from "@/lib/api";
import AlertItem from "@/components/AlertItem";
import { toast } from "sonner";

const FILTERS = [
  { value: "all", label: "All" },
  { value: "critical", label: "Critical" },
  { value: "warning", label: "Warnings" },
  { value: "info", label: "Positive" },
];

export default function Alerts() {
  const qc = useQueryClient();
  const { data: alerts = [], isLoading } = useQuery({ queryKey: ["alerts"], queryFn: () => fetchAlerts() });
  const [filter, setFilter] = useState("all");

  const dismiss = useMutation({
    mutationFn: (a) => dismissAlert(a.id),
    onSuccess: () => {
      toast.success("Alert dismissed");
      qc.invalidateQueries({ queryKey: ["alerts"] });
    },
  });

  const filtered = filter === "all" ? alerts : alerts.filter((a) => a.severity === filter);

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => {
          const active = filter === f.value;
          const count = f.value === "all" ? alerts.length : alerts.filter((a) => a.severity === f.value).length;
          return (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              data-testid={`alerts-filter-${f.value}`}
              className={`h-9 px-3.5 rounded-md text-sm font-medium border transition-colors inline-flex items-center gap-2 ${
                active
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {f.label}
              <span
                className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${
                  active ? "bg-white/15 text-white" : "bg-slate-100 text-slate-500"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-sm text-slate-500">Loading alerts…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">No alerts in this filter.</div>
        ) : (
          filtered.map((a) => (
            <AlertItem
              key={a.id}
              alert={a}
              testId={`alerts-row-${a.id}`}
              onDismiss={(x) => dismiss.mutate(x)}
            />
          ))
        )}
      </div>
    </div>
  );
}
